import { useEffect, useRef } from "react";
import { C } from "../constants/colors";
import { IN, OUT, CACHE, fmtCost, fmtTok, Swatch } from "./UsageByModule";

/* Cost by PERSON, with what she put in kept apart from what Brill wrote — the
   same split By Module has (her ask, 2026-10-08: "what they put in versus what
   Brill spit out… already in the by module but not by person"). Mostly input
   is context being re-sent (a pasted transcript riding every turn); mostly
   output is Brill writing for her. One blended dollar figure cannot tell them
   apart, and they call for opposite fixes.

   `focusId`: the person she clicked on the Users tab — scrolled to and ringed. */
export function UsageByUser({ users, focusId = null }) {
  const focusRef = useRef(null);
  useEffect(() => {
    if (focusRef.current) focusRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [focusId]);

  if (!users.length) return (
    <p style={{ fontSize: 13, color: "#bbb", textAlign: "center", padding: "40px 0" }}>No usage data yet.</p>
  );
  const maxCost = Math.max(...users.map(u => u.cost), 0.0001);
  const maxPerCall = Math.max(...users.map(u => u.inputPerCall || 0), 1);

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <Swatch color={IN} label="what they put in" />
        <Swatch color={OUT} label="what Brill wrote" />
        <Swatch color={CACHE} label="cache" />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {users.map((u, i) => {
          const total = u.cost || 0.0001;
          const pct = (v) => `${((v || 0) / total) * 100}%`;
          const focused = u.id === focusId;
          return (
            <div key={u.id} ref={focused ? focusRef : null} style={{
              background: "#fff", borderRadius: 10, padding: "12px 14px",
              border: focused ? `2px solid ${C.teal}` : "1px solid #e8e8f0",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7, gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 11, color: "#bbb", fontWeight: 700, flexShrink: 0 }}>#{i + 1}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.indigo, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</span>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.indigo }}>{fmtCost(u.cost)}</span>
                  <span style={{ fontSize: 11, color: "#999" }}>{u.calls} calls</span>
                </div>
              </div>

              <div style={{ background: "#f0eff6", borderRadius: 4, height: 6, overflow: "hidden", marginBottom: 6 }}>
                <div style={{ display: "flex", height: "100%", width: `${(u.cost / maxCost) * 100}%` }}>
                  <div style={{ background: IN, width: pct(u.inputCost) }} />
                  <div style={{ background: OUT, width: pct(u.outputCost) }} />
                  <div style={{ background: CACHE, width: pct(u.cacheCost) }} />
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", fontSize: 11, color: "#888" }}>
                <span>
                  in {fmtCost(u.inputCost)} ({fmtTok(u.inputTokens)}) · out {fmtCost(u.outputCost)} ({fmtTok(u.outputTokens)})
                  {u.cacheReadTokens + u.cacheWriteTokens > 0
                    ? ` · cache ${fmtCost(u.cacheCost)} (${fmtTok(u.cacheReadTokens)} read)`
                    : ""}
                </span>
                <span style={{ fontWeight: 600, color: (u.inputPerCall || 0) >= maxPerCall * 0.5 ? IN : "#888" }}>
                  {fmtCost(u.costPerCall || 0)}/call · {fmtTok(u.inputPerCall || 0)} in/call
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
